import React, { useMemo, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { ShieldAlert, UserPlus } from "lucide-react";
import AddMemberModal from "./AddMemberModal";

export default function MembershipsTab({ members, membershipRequests, onRefresh }) {
  const [squareOnly, setSquareOnly] = useState(false);
  const [decisionModal, setDecisionModal] = useState(null);
  const [decisionBusy, setDecisionBusy] = useState(false);
  const [terminateModal, setTerminateModal] = useState(null);
  const [terminateBusy, setTerminateBusy] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);

  const { pendingReqs, decidedReqs, realMembers, squarePendingCount, visibleMembers } = useMemo(() => {
    const pending = membershipRequests.filter((r) => r.status === "pending");
    const decided = membershipRequests.filter((r) => r.status !== "pending");
    const real = members.filter((m) => !m.is_admin_mirror);
    const squareCount = real.filter((m) => (m.pending_payment_method || "").startsWith("square_")).length;
    const visible = squareOnly
      ? real.filter((m) => (m.pending_payment_method || "").startsWith("square_"))
      : real;
    return { pendingReqs: pending, decidedReqs: decided, realMembers: real, squarePendingCount: squareCount, visibleMembers: visible };
  }, [members, membershipRequests, squareOnly]);

  const decideRequest = (id, action, name, email, note) =>
    setDecisionModal({ id, action, name, email, note });

  const confirmDecision = async () => {
    if (!decisionModal) return;
    const { id, action, email } = decisionModal;
    setDecisionBusy(true);
    try {
      await api.post(`/admin/membership-requests/${id}/${action}`);
      toast.success(`${action === "approve" ? "Approved" : "Denied"} · ${email}`);
      setDecisionModal(null);
      await onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to ${action}`);
    } finally {
      setDecisionBusy(false);
    }
  };

  const grantTier = async (memberId, email, tier) => {
    const priceMap = { basic: 50, accountability: 100, advance: 150 };
    const tierLabel = `${tier.charAt(0).toUpperCase() + tier.slice(1)} ($${priceMap[tier]} · 30 days)`;
    if (!window.confirm(`Confirm Square payment received for ${email} and grant ${tierLabel}?\n\nThey'll get 30 days of access starting now and an activation email.`)) return;
    try {
      await api.post(`/admin/members/${memberId}/grant-tier`, { tier });
      toast.success(`${email} activated · ${tier}`);
      await onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to grant tier");
    }
  };

  const terminateMember = (memberId, email) => {
    setTerminateModal({ id: memberId, email });
  };

  const confirmTerminate = async () => {
    if (!terminateModal) return;
    setTerminateBusy(true);
    try {
      await api.delete(`/admin/members/${terminateModal.id}`);
      toast.success(`${terminateModal.email} terminated`);
      setTerminateModal(null);
      await onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setTerminateBusy(false);
    }
  };

  return (
    <div data-testid="tab-memberships">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
        <h2 className="font-display text-3xl">Memberships</h2>
        <button
          onClick={() => setShowAddMember(true)}
          data-testid="open-add-member"
          className="inline-flex items-center gap-2 bg-[#E5B43A] text-black px-4 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-white transition-colors"
        >
          <UserPlus size={14} /> Add Member
        </button>
      </div>
      <p className="text-zinc-500 text-sm mb-8">All paying members, plus pending 1-on-1 requests. Terminate to delete a member and ALL their data.</p>

      {pendingReqs.length > 0 ? (
        <div className="border border-[#E5B43A] bg-[#E5B43A]/5 mb-8" data-testid="pending-requests">
          <div className="px-5 py-3 border-b border-[#E5B43A]/40 text-[10px] tracking-[0.3em] uppercase text-[#E5B43A]">
            Pending 1-on-1 Requests ({pendingReqs.length})
          </div>
          <table className="w-full text-sm min-w-[640px]">
            <tbody>
              {pendingReqs.map((r) => (
                <tr key={r.id} className="border-b border-zinc-900 last:border-0" data-testid={`request-row-${r.id}`}>
                  <td className="px-5 py-4">
                    <div className="font-semibold">{r.member_name}</div>
                    <div className="text-xs text-zinc-500">{r.member_email}</div>
                    {r.note && <div className="text-xs text-zinc-400 italic mt-1">&ldquo;{r.note}&rdquo;</div>}
                  </td>
                  <td className="px-5 py-4 text-xs text-zinc-500 text-right whitespace-nowrap">{new Date(r.created_at).toLocaleDateString()}</td>
                  <td className="px-5 py-4 text-right whitespace-nowrap">
                    <button onClick={() => decideRequest(r.id, "approve", r.member_name, r.member_email, r.note)} data-testid={`approve-${r.id}`} className="mr-2 bg-[#E5B43A] text-black px-3 py-1.5 text-xs font-bold uppercase tracking-widest hover:bg-white hover:text-black transition-colors">Approve</button>
                    <button onClick={() => decideRequest(r.id, "deny", r.member_name, r.member_email, r.note)} data-testid={`deny-${r.id}`} className="border border-zinc-700 text-zinc-300 px-3 py-1.5 text-xs font-bold uppercase tracking-widest hover:border-[#E5B43A] hover:text-[#E5B43A] transition-colors">Deny</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="border border-zinc-800 bg-[#141414] mb-8 px-5 py-6" data-testid="pending-requests-empty">
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-1">Pending 1-on-1 Requests</div>
          <div className="text-sm text-zinc-400">No applications waiting right now. New 1-on-1 requests from members will show up here for you to <span className="text-[#E5B43A] font-semibold">Approve</span> or <span className="text-[#E5B43A] font-semibold">Deny</span>.</div>
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <h3 className="font-display text-xl">All Members ({visibleMembers.length}{squareOnly ? ` of ${realMembers.length}` : ""})</h3>
        <button
          onClick={() => setSquareOnly((s) => !s)}
          data-testid="filter-square-pending"
          className={`inline-flex items-center gap-2 px-3 py-1.5 text-[10px] font-bold tracking-[0.3em] uppercase border transition-colors ${
            squareOnly ? "border-amber-500 bg-amber-500/10 text-amber-300" : "border-zinc-700 text-zinc-400 hover:border-amber-500 hover:text-amber-300"
          }`}
        >
          <span className={`inline-block w-1.5 h-1.5 rounded-full ${squareOnly ? "bg-amber-400" : "bg-zinc-600"}`} />
          Square Pending{squarePendingCount > 0 ? ` · ${squarePendingCount}` : ""}
          {squareOnly && <span className="ml-1 text-zinc-500">× clear</span>}
        </button>
      </div>

      <div className="border border-zinc-800 bg-[#141414] mb-10 overflow-hidden">
        <div className="max-h-[60vh] overflow-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="sticky top-0 bg-[#0A0A0A] z-10">
              <tr className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 border-b border-zinc-800">
                <th className="text-left px-5 py-3">Name</th>
                <th className="text-left px-5 py-3">Email</th>
                <th className="text-left px-5 py-3">Tier</th>
                <th className="text-left px-5 py-3 hidden md:table-cell">Active Until</th>
                <th className="text-left px-5 py-3 hidden md:table-cell">Joined</th>
                <th className="text-left px-5 py-3 hidden md:table-cell">Waiver</th>
                <th className="text-right px-5 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {visibleMembers.length === 0 ? (
                <tr><td colSpan={7} className="px-5 py-10 text-center text-zinc-500 text-sm">{squareOnly ? "No Square payments waiting right now." : "No members yet."}</td></tr>
              ) : visibleMembers.map((m) => <MemberRow key={m.id} m={m} grantTier={grantTier} terminateMember={terminateMember} />)}
            </tbody>
          </table>
        </div>
      </div>

      {decidedReqs.length > 0 && (
        <div data-testid="decided-requests">
          <h3 className="font-display text-xl mb-3">Past Requests ({decidedReqs.length})</h3>
          <div className="border border-zinc-800 bg-[#141414] overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <tbody>
                {decidedReqs.map((r) => (
                  <tr key={r.id} className="border-b border-zinc-900 last:border-0">
                    <td className="px-5 py-3">{r.member_name} <span className="text-zinc-500 text-xs">· {r.member_email}</span></td>
                    <td className="px-5 py-3 text-right">
                      <span className={`text-[10px] tracking-widest uppercase ${r.status === "approved" ? "text-[#E5B43A]" : "text-zinc-500"}`}>{r.status}</span>
                    </td>
                    <td className="px-5 py-3 text-xs text-zinc-500 text-right whitespace-nowrap">{r.decided_at ? new Date(r.decided_at).toLocaleDateString() : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {decisionModal && (
        <DecisionModal modal={decisionModal} busy={decisionBusy} onConfirm={confirmDecision} onClose={() => !decisionBusy && setDecisionModal(null)} />
      )}
      {terminateModal && (
        <TerminateModal
          modal={terminateModal}
          busy={terminateBusy}
          onConfirm={confirmTerminate}
          onClose={() => !terminateBusy && setTerminateModal(null)}
        />
      )}
      {showAddMember && (
        <AddMemberModal
          onClose={() => setShowAddMember(false)}
          onCreated={onRefresh}
        />
      )}
    </div>
  );
}

function MemberRow({ m, grantTier, terminateMember }) {
  const isActive = m.subscription_until && new Date(m.subscription_until) > new Date();
  const pendingTier = (m.pending_payment_method || "").replace("square_", "");
  const validPending = ["basic", "accountability", "advance"].includes(pendingTier) ? pendingTier : null;
  return (
    <tr className="border-b border-zinc-900 hover:bg-[#1a1a1a]" data-testid={`member-row-${m.id}`}>
      <td className="px-5 py-4 font-semibold">{m.name}</td>
      <td className="px-5 py-4 text-zinc-300 break-all">{m.email}</td>
      <td className="px-5 py-4">
        <span className={`inline-block px-2 py-0.5 text-[10px] tracking-widest uppercase border ${
          m.tier === "one_on_one" ? "bg-[#E5B43A] border-[#E5B43A] text-white" :
          m.tier ? "border-[#E5B43A] text-[#E5B43A]" : "border-zinc-700 text-zinc-500"
        }`}>
          {m.tier ? m.tier.replace("_", " ") : "—"}
        </span>
        {(m.pending_payment_method || "").startsWith("square_") && (
          <span className="ml-2 inline-block px-2 py-0.5 text-[10px] tracking-widest uppercase border border-amber-500 text-amber-400" data-testid={`square-pending-${m.id}`}>
            Square · {m.pending_payment_method.replace("square_", "")}
          </span>
        )}
      </td>
      <td className="px-5 py-4 text-zinc-400 hidden md:table-cell whitespace-nowrap">
        {m.subscription_until ? (
          <span className={isActive ? "text-white" : "text-zinc-600"}>{new Date(m.subscription_until).toLocaleDateString()}</span>
        ) : "—"}
      </td>
      <td className="px-5 py-4 text-zinc-500 text-xs hidden md:table-cell whitespace-nowrap">{new Date(m.created_at).toLocaleDateString()}</td>
      <td className="px-5 py-4 hidden md:table-cell whitespace-nowrap" data-testid={`waiver-cell-${m.id}`}>
        {m.waiver_signed_at ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-[0.2em] uppercase text-emerald-400 border border-emerald-400/40 px-2 py-1" title={`Signed ${new Date(m.waiver_signed_at).toLocaleString()}`}>
            ✓ {new Date(m.waiver_signed_at).toLocaleDateString()}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500 border border-zinc-700 px-2 py-1">
            Not Signed
          </span>
        )}
      </td>
      <td className="px-5 py-4 text-right whitespace-nowrap">
        {!isActive && (
          validPending ? (
            <button onClick={() => grantTier(m.id, m.email, validPending)} data-testid={`grant-${validPending}-${m.id}`} className="mr-3 text-xs font-bold uppercase tracking-widest text-emerald-400 hover:text-white transition-colors">
              Grant {validPending.charAt(0).toUpperCase() + validPending.slice(1)}
            </button>
          ) : (
            <span className="mr-3 inline-flex gap-2">
              <button onClick={() => grantTier(m.id, m.email, "basic")} data-testid={`grant-basic-${m.id}`} className="text-xs font-bold uppercase tracking-widest text-emerald-400 hover:text-white transition-colors">Grant Basic</button>
              <button onClick={() => grantTier(m.id, m.email, "accountability")} data-testid={`grant-accountability-${m.id}`} className="text-xs font-bold uppercase tracking-widest text-emerald-400/70 hover:text-white transition-colors">+ Acc</button>
              <button onClick={() => grantTier(m.id, m.email, "advance")} data-testid={`grant-advance-${m.id}`} className="text-xs font-bold uppercase tracking-widest text-emerald-400/70 hover:text-white transition-colors">+ Adv</button>
            </span>
          )
        )}
        <button onClick={() => terminateMember(m.id, m.email)} data-testid={`terminate-${m.id}`} className="text-xs font-bold uppercase tracking-widest text-zinc-400 hover:text-[#E5B43A] transition-colors">
          Terminate
        </button>
      </td>
    </tr>
  );
}

function DecisionModal({ modal, busy, onConfirm, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      data-testid="decision-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full sm:max-w-lg border border-zinc-800 bg-[#0F0F0F] shadow-2xl">
        <div className={`px-6 py-4 border-b flex items-center gap-3 ${
          modal.action === "approve" ? "border-[#E5B43A]/40 bg-[#E5B43A]/10" : "border-zinc-800 bg-zinc-900/40"
        }`}>
          <div className={`w-2.5 h-2.5 rounded-full ${modal.action === "approve" ? "bg-[#E5B43A]" : "bg-zinc-500"}`} />
          <div className="text-[10px] tracking-[0.3em] uppercase">
            {modal.action === "approve" ? <span className="text-[#E5B43A] font-bold">Approve 1-on-1 Application</span> : <span className="text-zinc-400 font-bold">Deny 1-on-1 Application</span>}
          </div>
        </div>
        <div className="px-6 py-6 space-y-5">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-1">Applicant</div>
            <div className="font-display text-2xl leading-tight" data-testid="decision-modal-name">{modal.name}</div>
            <div className="text-sm text-zinc-400 break-all">{modal.email}</div>
          </div>
          {modal.note && (
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-1">Their Note</div>
              <div className="text-sm text-zinc-300 italic border-l-2 border-zinc-700 pl-3 py-1">&ldquo;{modal.note}&rdquo;</div>
            </div>
          )}
          <div className="border border-zinc-800 bg-[#141414] px-4 py-3">
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">What happens next</div>
            {modal.action === "approve" ? (
              <ul className="space-y-1.5 text-sm text-zinc-300">
                <li className="flex gap-2"><span className="text-[#E5B43A] shrink-0">→</span><span>Tier flipped to <b className="text-white">1-on-1</b>, active for 30 days</span></li>
                <li className="flex gap-2"><span className="text-[#E5B43A] shrink-0">→</span><span>Full access unlocked: Workouts, Meal Plans, Progress, Billing</span></li>
                <li className="flex gap-2"><span className="text-[#E5B43A] shrink-0">→</span><span>Approval email sent to <b className="text-white break-all">{modal.email}</b></span></li>
              </ul>
            ) : (
              <ul className="space-y-1.5 text-sm text-zinc-300">
                <li className="flex gap-2"><span className="text-zinc-500 shrink-0">→</span><span>Request marked <b className="text-white">denied</b>, no tier change</span></li>
                <li className="flex gap-2"><span className="text-zinc-500 shrink-0">→</span><span>Polite denial email sent to <b className="text-white break-all">{modal.email}</b></span></li>
                <li className="flex gap-2"><span className="text-zinc-500 shrink-0">→</span><span>They can still pick Basic / Accountability / Advance</span></li>
              </ul>
            )}
          </div>
        </div>
        <div className="px-6 py-4 border-t border-zinc-800 bg-[#0A0A0A] flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          <button onClick={onClose} disabled={busy} data-testid="decision-cancel" className="px-5 py-3 text-xs font-bold uppercase tracking-widest border border-zinc-700 text-zinc-300 hover:border-white hover:text-white transition-colors disabled:opacity-50">Cancel</button>
          <button onClick={onConfirm} disabled={busy} data-testid="decision-confirm" className={`px-5 py-3 text-xs font-bold uppercase tracking-widest transition-colors disabled:opacity-60 ${
            modal.action === "approve" ? "bg-[#E5B43A] text-black hover:bg-white hover:text-black" : "bg-zinc-200 text-black hover:bg-white"
          }`}>
            {busy ? "Sending..." : modal.action === "approve" ? "Confirm Approve" : "Confirm Deny"}
          </button>
        </div>
      </div>
    </div>
  );
}

function TerminateModal({ modal, busy, onConfirm, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      data-testid="terminate-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full sm:max-w-lg border border-[#E5B43A] bg-[#0F0F0F] shadow-2xl">
        <div className="px-6 py-4 border-b border-[#E5B43A]/40 bg-[#E5B43A]/10 flex items-center gap-3">
          <ShieldAlert className="text-[#E5B43A] shrink-0" size={18} />
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#E5B43A] font-bold">Terminate Member · Irreversible</div>
        </div>
        <div className="px-6 py-6 space-y-5">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-1">Member</div>
            <div className="font-display text-xl md:text-2xl leading-tight break-all">{modal.email}</div>
          </div>
          <div className="border border-zinc-800 bg-[#141414] px-4 py-3">
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">This will permanently delete</div>
            <ul className="space-y-1.5 text-sm text-zinc-300">
              <li className="flex gap-2"><span className="text-[#E5B43A] shrink-0">×</span><span>Their account &amp; login</span></li>
              <li className="flex gap-2"><span className="text-[#E5B43A] shrink-0">×</span><span>All meal plans &amp; workouts</span></li>
              <li className="flex gap-2"><span className="text-[#E5B43A] shrink-0">×</span><span>All progress photos &amp; check-ins</span></li>
              <li className="flex gap-2"><span className="text-[#E5B43A] shrink-0">×</span><span>All billing history &amp; invoices</span></li>
              <li className="flex gap-2"><span className="text-[#E5B43A] shrink-0">×</span><span>Any pending 1-on-1 requests</span></li>
            </ul>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-zinc-800 bg-[#0A0A0A] flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          <button onClick={onClose} disabled={busy} data-testid="terminate-cancel" className="px-5 py-3 text-xs font-bold uppercase tracking-widest border border-zinc-700 text-zinc-300 hover:border-white hover:text-white transition-colors disabled:opacity-50">Cancel</button>
          <button onClick={onConfirm} disabled={busy} data-testid="terminate-confirm" className="px-5 py-3 text-xs font-bold uppercase tracking-widest bg-[#E5B43A] text-black hover:bg-white hover:text-black transition-colors disabled:opacity-60">
            {busy ? "Terminating..." : "Terminate Permanently"}
          </button>
        </div>
      </div>
    </div>
  );
}
